import {
  QueryResult,
  MutationResult,
  ApolloError,
  OperationVariables,
  ServerError,
  LazyQueryResult,
} from '@apollo/client';
import { useEffect } from 'react';
import { errors } from './errors';
import { useAuth0 } from '@auth0/auth0-react';
import { useToasts } from '../../graphql/toasts/useToasts';
import { ConfigContext } from '../../providers/ConfigContext';

export const DEFAULT_ERROR = 'Something went wrong, please try again';

export const useErrorHandler = <
  TData = any,
  TVariables extends OperationVariables = Record<string, any>,
>(
  queryResult:
    | QueryResult<TData, TVariables>
    | MutationResult<TData>
    | LazyQueryResult<TData, TVariables>,
) => {
  const { pushToast } = useToasts();
  const { logout } = useAuth0();
  const config = ConfigContext.useContainer();
  const { data, error, loading } = queryResult;

  useEffect(() => {
    if (error && !loading) {
      if (isUnauthorizedError(error)) {
        logout({ logoutParams: { returnTo: config.auth0.logout_uri } });
      }

      const errorMessage = Object.keys(errors).includes(error.message)
        ? errors[error.message]
        : error.message;
      pushToast({
        variables: { toast: { message: errorMessage || DEFAULT_ERROR } },
      });
    }
  }, [loading, error, pushToast, logout, config]);

  return { data, error, loading };
};

function isUnauthorizedError(error: ApolloError): boolean {
  return (error.networkError as ServerError)?.statusCode === 401;
}
